/**
 * @fileoverview ZoomContainer Web Component for product image zoom functionality
 * Provides desktop modal zoom and mobile fullscreen zoom experiences
 * @version 1.0.0
 * @author Greyson Clothiers Development Team
 */

if (!customElements.get("zoom-container")) {
  /**
   * ZoomContainer Web Component (Modernized)
   *
   * A custom HTML element that provides image zoom functionality for product images.
   * Supports both desktop modal zoom with thumbnails and mobile fullscreen zoom.
   *
   * @class ZoomContainer
   * @extends {HTMLElement}
   *
   * @example
   * <!-- HTML Usage -->
   * <zoom-container
   *   data-image-index="0"
   *   data-product-title="Product Name"
   *   data-zoom-image="/path/to/large-image.jpg"
   *   data-thumbnail-image="/path/to/thumb.jpg">
   *   <div class="js-zoom"></div>
   * </zoom-container>
   *
   * @requires window.theme.utilities.isMobile() - Theme utility function for mobile detection
   * @requires window.theme.elements.svg_close - Theme SVG close icon
   * @requires window.theme.elements.svg_arrow_up - Theme SVG arrow up icon
   */
  class ZoomContainer extends HTMLElement {
    /**
     * Configuration constants for better maintainability
     * @private
     * @static
     */
    static #CONFIG = {
      SELECTORS: {
        ZOOM_BACKGROUND: '.js-zoom',
        DESKTOP_TEMPLATE: '#zoom-container-desktop-template',
        THUMBNAIL_TEMPLATE: '#zoom-container-desktop-thumbnail-template',
        ACTIVE_SLIDES: '.product__slide.js-main-slide .zoom-container',
        FALLBACK_SLIDES: '.product__slide .zoom-container',
        ZOOMED_PANEL: '.right-panel-zoomed',
        THUMBNAILS: '.image-thumbnail',
        CLOSE_BUTTON: '.product-media-modal-desktop__toggle',
        THUMBNAIL_CONTAINER: '.zoom-container-desktop .thumbnails-image-box',
        THUMBNAIL_ACTIVE: '.thumbnail-active'
      },
      CLASSES: {
        DESKTOP_CONTAINER: 'zoom-container-desktop',
        MOBILE_CONTAINER: 'zoom-container-mobile',
        NO_SCROLL: 'no-scroll',
        IS_ZOOMED: 'is-zoomed',
        THUMBNAIL_ACTIVE: 'thumbnail-active'
      },
      ATTRIBUTES: {
        IMAGE_INDEX: 'data-image-index',
        PRODUCT_TITLE: 'data-product-title',
        ZOOM_IMAGE: 'data-zoom-image',
        THUMBNAIL_IMAGE: 'data-thumbnail-image',
        ZOOMED_IMAGE: 'zoomed-image'
      },
      THUMBNAIL_SCROLL: {
        VISIBLE_COUNT: 4,
        SCROLL_BEHAVIOR: 'smooth'
      },
      KEYBOARD: {
        ESCAPE: 'Escape',
        ARROW_UP: 'ArrowUp',
        ARROW_DOWN: 'ArrowDown'
      }
    };

    /**
     * Creates an instance of ZoomContainer
     * Initializes the component and sets up click event listeners
     *
     * @constructor
     * @memberof ZoomContainer
     */
    constructor() {
      super();

      /** @type {HTMLElement|null} The zoom background element */
      this.background = this.querySelector(ZoomContainer.#CONFIG.SELECTORS.ZOOM_BACKGROUND);

      /** @type {boolean} Current zoom state */
      this.isZoomed = false;

      /** @type {AbortController} For cleaning up event listeners */
      this.abortController = new AbortController();

      /** @type {HTMLElement|null} Reference to active modal for cleanup */
      this.activeModal = null;

      // Set up click handler for zoom toggle with proper cleanup
      this.#setupEventListeners();
    }

    /**
     * Web Component lifecycle - called when element is removed from DOM
     * @private
     */
    disconnectedCallback() {
      this.#cleanup();
    }

    /**
     * Set up event listeners with proper cleanup support
     * @private
     */
    #setupEventListeners() {
      this.addEventListener("click", this.#handleClick.bind(this), {
        signal: this.abortController.signal
      });
    }

    /**
     * Handle click events with error boundary
     * @private
     * @param {Event} event - The click event
     */
    #handleClick(event) {
      try {
        // Prevent event bubbling if needed
        event.preventDefault();

        if (!this.#isMobile()) {
          this.toggleEventDesktop();
        } else {
          this.toggleEventMobile();
        }
      } catch (error) {
        console.error('ZoomContainer: Error handling click event', error);
        // Graceful degradation - could implement fallback behavior here
      }
    }

    /**
     * Modern mobile detection with fallback
     * @private
     * @returns {boolean}
     */
    #isMobile() {
      try {
        // Use theme utility if available, otherwise fallback to viewport detection
        return window.theme?.utilities?.isMobile?.() ?? window.innerWidth < 768;
      } catch (error) {
        console.warn('ZoomContainer: Theme utilities not available, using fallback mobile detection');
        return window.innerWidth < 768;
      }
    }

    /**
     * Clean up resources and event listeners
     * @private
     */
    #cleanup() {
      // Abort all event listeners
      this.abortController.abort();

      // Clean up any active modals
      if (this.activeModal) {
        try {
          this.activeModal.remove();
          document.body.classList.remove(ZoomContainer.#CONFIG.CLASSES.NO_SCROLL);
        } catch (error) {
          console.warn('ZoomContainer: Error during modal cleanup', error);
        }
      }
    }

    /**
     * Get template content safely
     * @private
     * @param {string} selector - Template selector
     * @returns {string|null} Template HTML or null if not found
     */
    #getTemplate(selector) {
      try {
        const template = document.getElementById(selector.replace('#', ''));
        if (!template) {
          console.warn(`ZoomContainer: Template not found: ${selector}`);
          return null;
        }
        return template.innerHTML;
      } catch (error) {
        console.error(`ZoomContainer: Error getting template ${selector}`, error);
        return null;
      }
    }

    /**
     * Safely get SVG icons from theme
     * @private
     * @param {string} iconName - Name of the icon
     * @returns {string} SVG HTML or fallback
     */
    #getSVGIcon(iconName) {
      try {
        return window.theme?.elements?.[iconName] ?? '✕'; // Unicode fallback
      } catch (error) {
        console.warn(`ZoomContainer: SVG icon not available: ${iconName}`);
        return '✕';
      }
    }

    /**
     * Updates the zoom background position based on mouse coordinates
     * Used for hover zoom effects on the main product image
     *
     * @method zoom
     * @memberof ZoomContainer
     * @param {MouseEvent} e - The mouse event containing offset coordinates
     * @returns {void}
     *
     * @example
     * // Typically called from mousemove event
     * zoomContainer.zoom(mouseEvent);
     */
    zoom(e) {
      if (!this.background) return;

      try {
        const offsetX = e.offsetX;
        const offsetY = e.offsetY;
        const x = (offsetX / this.background.offsetWidth) * 100;
        const y = (offsetY / this.background.offsetHeight) * 100;
        this.background.style.backgroundPosition = `${x}% ${y}%`;
      } catch (error) {
        console.warn('ZoomContainer: Error updating zoom position', error);
      }
    }

    /**
     * Creates and displays the desktop zoom modal (Modernized)
     * Builds a full-screen modal with large image, thumbnails, and navigation
     *
     * @method toggleEventDesktop
     * @memberof ZoomContainer
     * @returns {void}
     */
    toggleEventDesktop() {
      try {
        const imageIndex = this.dataset.imageIndex;
        const desktopTemplate = this.#getTemplate('zoom-container-desktop-template');
        const thumbnailTemplate = this.#getTemplate('zoom-container-desktop-thumbnail-template');

        if (!desktopTemplate || !thumbnailTemplate) {
          console.error('ZoomContainer: Required templates not found');
          return;
        }

        // Create modal container
        const modalContainer = document.createElement("div");
        modalContainer.classList.add(ZoomContainer.#CONFIG.CLASSES.DESKTOP_CONTAINER);
        this.activeModal = modalContainer;

        // Get active media slides with fallback
        const activeSlides = this.#getActiveMediaSlides();
        if (activeSlides.length === 0) {
          console.warn('ZoomContainer: No media slides found');
          return;
        }

        // Build thumbnails HTML
        const thumbnailsHTML = this.#buildThumbnailsHTML(activeSlides, thumbnailTemplate, imageIndex);

        // Replace template placeholders
        let modalHTML = desktopTemplate
          .replace(/ZoomContainerThumbnails/g, thumbnailsHTML)
          .replace(/ZoomContainerTitle/g, this.dataset.productTitle || '')
          .replace(/ZoomContainerImage/g, this.dataset.zoomImage)
          .replace(/zoomContainerClose/g, this.#getSVGIcon('svg_close'));

        modalContainer.innerHTML = modalHTML;
        document.body.appendChild(modalContainer);

        // Setup modal functionality
        this.#setupModalInteractions(modalContainer);

        // Prevent body scrolling
        document.body.classList.add(ZoomContainer.#CONFIG.CLASSES.NO_SCROLL);

      } catch (error) {
        console.error('ZoomContainer: Error creating desktop modal', error);
        this.#cleanup();
      }
    }

    /**
     * Get active media slides with fallback logic
     * @private
     * @returns {NodeListOf<Element>} Collection of slide elements
     */
    #getActiveMediaSlides() {
      const primarySlides = document.querySelectorAll(ZoomContainer.#CONFIG.SELECTORS.ACTIVE_SLIDES);
      return primarySlides.length > 0
        ? primarySlides
        : document.querySelectorAll(ZoomContainer.#CONFIG.SELECTORS.FALLBACK_SLIDES);
    }

    /**
     * Build thumbnails HTML from template
     * @private
     * @param {NodeListOf<Element>} slides - Media slide elements
     * @param {string} template - Thumbnail template HTML
     * @param {string} currentIndex - Current active image index
     * @returns {string} Built thumbnails HTML
     */
    #buildThumbnailsHTML(slides, template, currentIndex) {
      return Array.from(slides).map((slide, index) => {
        const isActive = currentIndex == slide.dataset.imageIndex;
        return template
          .replace(/ZoomContainerThumbnailActive/g, isActive ? ZoomContainer.#CONFIG.CLASSES.THUMBNAIL_ACTIVE : '')
          .replace(/ZoomContainerThumbnailId/g, `thumbnail-${index}`)
          .replace(/ZoomImageSource/g, slide.dataset.zoomImage)
          .replace(/ZoomContainerThumbnailImage/g, slide.dataset.thumbnailImage);
      }).join('');
    }

    /**
     * Setup all modal interactions (zoom, thumbnails, keyboard, close)
     * @private
     * @param {HTMLElement} modalContainer - The modal container element
     */
    #setupModalInteractions(modalContainer) {
      const zoomedPanel = modalContainer.querySelector(ZoomContainer.#CONFIG.SELECTORS.ZOOMED_PANEL);
      const thumbnails = modalContainer.querySelectorAll(ZoomContainer.#CONFIG.SELECTORS.THUMBNAILS);
      const closeButton = modalContainer.querySelector(ZoomContainer.#CONFIG.SELECTORS.CLOSE_BUTTON);

      if (zoomedPanel) {
        this.setupImageZoom(zoomedPanel);
      }

      if (thumbnails.length > 0) {
        this.setupThumbnailClick(zoomedPanel, thumbnails);
      }

      if (thumbnails.length > ZoomContainer.#CONFIG.THUMBNAIL_SCROLL.VISIBLE_COUNT) {
        this.setupThumbnailScrolling();
      }

      // Setup close functionality with keyboard support
      this.#setupModalClose(closeButton);
    }

    /**
     * Setup modal close functionality with keyboard support
     * @private
     * @param {HTMLElement} closeButton - The close button element
     */
    #setupModalClose(closeButton) {
      const closeModal = () => {
        if (this.activeModal) {
          this.activeModal.remove();
          this.activeModal = null;
          document.body.classList.remove(ZoomContainer.#CONFIG.CLASSES.NO_SCROLL);
        }
      };

      // Click handler
      if (closeButton) {
        closeButton.addEventListener('click', closeModal);
      }

      // Keyboard handler (Escape key)
      const keyHandler = (event) => {
        if (event.key === ZoomContainer.#CONFIG.KEYBOARD.ESCAPE) {
          closeModal();
          document.removeEventListener('keydown', keyHandler);
        }
      };
      document.addEventListener('keydown', keyHandler);
    }

    /**
     * Sets up mouse-based zoom functionality for the main zoomed image (Enhanced)
     * Centers the image initially and adds hover zoom effects
     *
     * @method setupImageZoom
     * @memberof ZoomContainer
     * @param {HTMLElement} zoomedObject - The container element for the zoomed image
     * @returns {void}
     */
    setupImageZoom(zoomedObject) {
      if (!zoomedObject) return;

      try {
        const img = zoomedObject.querySelector('img');
        if (!img) return;

        // Center image initially
        zoomedObject.scrollLeft = (img.width - zoomedObject.clientWidth) / 2;
        zoomedObject.scrollTop = (img.height - zoomedObject.clientHeight) / 2;

        // Enhanced mouse interactions with error handling
        const mouseMoveHandler = (event) => {
          try {
            const offsetX = event.offsetX;
            const offsetY = event.offsetY;
            const x = (offsetX / zoomedObject.offsetWidth) * 100;
            const y = (offsetY / zoomedObject.offsetHeight) * 100;
            zoomedObject.style.backgroundPosition = `${x}% ${y}%`;
            zoomedObject.classList.add(ZoomContainer.#CONFIG.CLASSES.IS_ZOOMED);
          } catch (error) {
            console.warn('ZoomContainer: Error in mousemove handler', error);
          }
        };

        img.addEventListener('mousemove', mouseMoveHandler);
        img.addEventListener('mouseover', () => {
          zoomedObject.classList.add(ZoomContainer.#CONFIG.CLASSES.IS_ZOOMED);
        });
        img.addEventListener('mouseout', () => {
          zoomedObject.classList.remove(ZoomContainer.#CONFIG.CLASSES.IS_ZOOMED);
        });

      } catch (error) {
        console.error('ZoomContainer: Error setting up image zoom', error);
      }
    }

    /**
     * Sets up thumbnail click handlers and keyboard navigation (Enhanced)
     * Enables switching between images via clicks and arrow keys
     *
     * @method setupThumbnailClick
     * @memberof ZoomContainer
     * @param {HTMLElement} zoomedObject - The main zoom container
     * @param {NodeListOf<HTMLElement>} thumbnails - Collection of thumbnail elements
     * @returns {void}
     */
    setupThumbnailClick(zoomedObject, thumbnails) {
      if (!zoomedObject || !thumbnails?.length) return;

      try {
        // Enhanced click handlers
        thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => {
            this.#switchToThumbnail(thumbnail, thumbnails, zoomedObject);
          });

          // Add keyboard navigation to thumbnails
          thumbnail.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.#switchToThumbnail(thumbnail, thumbnails, zoomedObject);
            }
          });

          // Make thumbnails focusable
          thumbnail.setAttribute('tabindex', '0');
        });

        // Enhanced keyboard navigation
        this.#setupKeyboardNavigation(thumbnails, zoomedObject);

      } catch (error) {
        console.error('ZoomContainer: Error setting up thumbnail interactions', error);
      }
    }

    /**
     * Switch to a specific thumbnail
     * @private
     * @param {HTMLElement} selectedThumbnail - The selected thumbnail
     * @param {NodeListOf<HTMLElement>} allThumbnails - All thumbnail elements
     * @param {HTMLElement} zoomedObject - The zoom container
     */
    #switchToThumbnail(selectedThumbnail, allThumbnails, zoomedObject) {
      try {
        // Remove active class from all thumbnails
        const currentActive = document.querySelector(ZoomContainer.#CONFIG.SELECTORS.THUMBNAIL_ACTIVE);
        if (currentActive) {
          currentActive.classList.remove(ZoomContainer.#CONFIG.CLASSES.THUMBNAIL_ACTIVE);
        }

        // Get new image source
        const newImageSrc = selectedThumbnail.querySelector('img')?.getAttribute(ZoomContainer.#CONFIG.ATTRIBUTES.ZOOMED_IMAGE);
        if (!newImageSrc) return;

        // Update zoom container
        const zoomImg = zoomedObject?.querySelector('img');
        if (zoomImg) {
          zoomImg.setAttribute('src', newImageSrc);
          zoomedObject.style.backgroundImage = `url('${newImageSrc}')`;
        }

        // Set new active state
        const thumbnailImg = selectedThumbnail.querySelector('img');
        if (thumbnailImg) {
          thumbnailImg.classList.add(ZoomContainer.#CONFIG.CLASSES.THUMBNAIL_ACTIVE);
        }

      } catch (error) {
        console.error('ZoomContainer: Error switching thumbnail', error);
      }
    }

    /**
     * Setup enhanced keyboard navigation
     * @private
     * @param {NodeListOf<HTMLElement>} thumbnails - Thumbnail elements
     * @param {HTMLElement} zoomedObject - Zoom container
     */
    #setupKeyboardNavigation(thumbnails, zoomedObject) {
      const keyHandler = (event) => {
        if (event.key !== ZoomContainer.#CONFIG.KEYBOARD.ARROW_UP &&
            event.key !== ZoomContainer.#CONFIG.KEYBOARD.ARROW_DOWN) return;

        event.preventDefault();

        try {
          const activeThumbnail = document.querySelector(ZoomContainer.#CONFIG.SELECTORS.THUMBNAIL_ACTIVE);
          if (!activeThumbnail) return;

          const currentIndex = Array.from(thumbnails).findIndex(thumb =>
            thumb.querySelector('img') === activeThumbnail
          );

          let newIndex = currentIndex;
          if (event.key === ZoomContainer.#CONFIG.KEYBOARD.ARROW_UP && currentIndex > 0) {
            newIndex = currentIndex - 1;
          } else if (event.key === ZoomContainer.#CONFIG.KEYBOARD.ARROW_DOWN && currentIndex < thumbnails.length - 1) {
            newIndex = currentIndex + 1;
          }

          if (newIndex !== currentIndex) {
            this.#switchToThumbnail(thumbnails[newIndex], thumbnails, zoomedObject);
            this.#scrollThumbnailIntoView(thumbnails[newIndex]);
          }

        } catch (error) {
          console.error('ZoomContainer: Error in keyboard navigation', error);
        }
      };

      document.addEventListener('keydown', keyHandler);
    }

    /**
     * Scroll thumbnail into view when navigating with keyboard
     * @private
     * @param {HTMLElement} thumbnail - Thumbnail to scroll to
     */
    #scrollThumbnailIntoView(thumbnail) {
      try {
        const container = document.querySelector(ZoomContainer.#CONFIG.SELECTORS.THUMBNAIL_CONTAINER);
        if (!container || !thumbnail) return;

        const thumbnailRect = thumbnail.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        if (thumbnailRect.top < containerRect.top || thumbnailRect.bottom > containerRect.bottom) {
          thumbnail.scrollIntoView({
            behavior: ZoomContainer.#CONFIG.THUMBNAIL_SCROLL.SCROLL_BEHAVIOR,
            block: 'nearest'
          });
        }
      } catch (error) {
        console.warn('ZoomContainer: Error scrolling thumbnail into view', error);
      }
    }

    /**
     * Sets up scrollable thumbnail navigation for desktop zoom modal (Enhanced)
     * Creates arrow controls and smooth scrolling for thumbnail list
     *
     * @method setupThumbnailScrolling
     * @memberof ZoomContainer
     * @returns {void}
     */
    setupThumbnailScrolling() {
      try {
        const container = document.querySelector(ZoomContainer.#CONFIG.SELECTORS.THUMBNAIL_CONTAINER);
        const thumbnails = Array.from(container?.querySelectorAll(ZoomContainer.#CONFIG.SELECTORS.THUMBNAILS) || []);

        if (!container || thumbnails.length === 0) return;

        const config = ZoomContainer.#CONFIG.THUMBNAIL_SCROLL;
        const thumbnailHeight = thumbnails[0]?.getBoundingClientRect().height || 0;
        const thumbnailStyle = window.getComputedStyle(thumbnails[0]);
        const margins = parseFloat(thumbnailStyle.marginTop) + parseFloat(thumbnailStyle.marginBottom);

        // Setup smooth scrolling
        container.style.scrollBehavior = config.SCROLL_BEHAVIOR;
        container.style.height = `${(thumbnailHeight + margins) * config.VISIBLE_COUNT}px`;

        // Add navigation arrows with better icons
        const arrowsHTML = `
          <span class="up-arrow arrow" role="button" tabindex="0" aria-label="Previous images">
            ${this.#getSVGIcon('svg_arrow_up')}
          </span>
          <span class="down-arrow arrow" role="button" tabindex="0" aria-label="Next images">
            ${this.#getSVGIcon('svg_arrow_up')}
          </span>
        `;
        container.insertAdjacentHTML('afterend', arrowsHTML);

        // Setup arrow functionality
        this.#setupScrollArrows(container, thumbnailHeight + margins);

        // Auto-scroll to active thumbnail
        this.#scrollToActiveThumbnail(container, thumbnails, thumbnailHeight + margins);

      } catch (error) {
        console.error('ZoomContainer: Error setting up thumbnail scrolling', error);
      }
    }

    /**
     * Setup scroll arrow functionality
     * @private
     * @param {HTMLElement} container - Thumbnail container
     * @param {number} itemHeight - Height of each thumbnail including margins
     */
    #setupScrollArrows(container, itemHeight) {
      const upArrow = document.querySelector('.up-arrow');
      const downArrow = document.querySelector('.down-arrow');

      if (!upArrow || !downArrow) return;

      const updateArrows = () => {
        const { scrollTop } = container;
        const maxScroll = container.scrollHeight - container.clientHeight;

        upArrow.style.display = scrollTop === 0 ? 'none' : 'inline';
        downArrow.style.display = scrollTop >= maxScroll ? 'none' : 'inline';
      };

      const scrollUp = () => {
        container.scrollTop -= itemHeight;
        updateArrows();
      };

      const scrollDown = () => {
        container.scrollTop += itemHeight;
        updateArrows();
      };

      // Mouse and keyboard handlers
      upArrow.addEventListener('click', scrollUp);
      downArrow.addEventListener('click', scrollDown);

      upArrow.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          scrollUp();
        }
      });

      downArrow.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          scrollDown();
        }
      });

      container.addEventListener('scroll', updateArrows);
      updateArrows(); // Initial state
    }

    /**
     * Auto-scroll to active thumbnail on modal open
     * @private
     * @param {HTMLElement} container - Thumbnail container
     * @param {Array<HTMLElement>} thumbnails - Thumbnail elements
     * @param {number} itemHeight - Height of each item
     */
    #scrollToActiveThumbnail(container, thumbnails, itemHeight) {
      try {
        const activeIndex = thumbnails.findIndex(thumb =>
          thumb.querySelector(`.${ZoomContainer.#CONFIG.CLASSES.THUMBNAIL_ACTIVE}`)
        );

        if (activeIndex >= ZoomContainer.#CONFIG.THUMBNAIL_SCROLL.VISIBLE_COUNT) {
          const scrollPosition = (activeIndex - ZoomContainer.#CONFIG.THUMBNAIL_SCROLL.VISIBLE_COUNT + 1) * itemHeight;
          container.scrollTop = scrollPosition;
        }
      } catch (error) {
        console.warn('ZoomContainer: Error auto-scrolling to active thumbnail', error);
      }
    }

    /**
     * Creates and displays mobile fullscreen zoom view (Enhanced)
     * Shows a simple fullscreen overlay with the zoomed image
     *
     * @method toggleEventMobile
     * @memberof ZoomContainer
     * @returns {void}
     */
    toggleEventMobile() {
      try {
        const container = document.createElement("div");
        container.classList.add(ZoomContainer.#CONFIG.CLASSES.MOBILE_CONTAINER);

        // Enhanced mobile HTML with better accessibility
        container.innerHTML = `
          <button class="product-media-modal__toggle"
                  aria-label="Close zoom view"
                  type="button">
            ${this.#getSVGIcon('svg_close')}
          </button>
          <img src="${this.dataset.zoomImage}"
               alt="${this.dataset.productTitle || ''}"
               width=""
               height="" />
        `;

        document.body.appendChild(container);
        this.activeModal = container;

        // Center the image
        const img = container.querySelector('img');
        if (img) {
          container.scrollLeft = (img.width - container.clientWidth) / 2;
          container.scrollTop = (img.height - container.clientHeight) / 2;
        }

        // Setup close handlers
        const closeButton = container.querySelector('.product-media-modal__toggle');
        const closeModal = () => {
          container.remove();
          this.activeModal = null;
        };

        closeButton?.addEventListener('click', closeModal);
        container.addEventListener('click', (e) => {
          if (e.target === container || e.target === img) {
            closeModal();
          }
        });

        // Keyboard close
        const keyHandler = (event) => {
          if (event.key === ZoomContainer.#CONFIG.KEYBOARD.ESCAPE) {
            closeModal();
            document.removeEventListener('keydown', keyHandler);
          }
        };
        document.addEventListener('keydown', keyHandler);

      } catch (error) {
        console.error('ZoomContainer: Error creating mobile zoom', error);
      }
    }
  }

  /**
   * Register the ZoomContainer as a custom element
   * Makes <zoom-container> available throughout the application
   */
  customElements.define("zoom-container", ZoomContainer);
}
